import React from 'react'
import { useTranslation } from 'react-i18next'
import ServiceLayout from '../components/ServiceLayout'
import { motion } from 'framer-motion'
import { Cloud, Shield, HardDrive } from 'lucide-react'

const DataSecurityPage = () => {
  const { t } = useTranslation()
  const dataSecurity = t('dataSecurity', { returnObjects: true })

  const parentBreadcrumbs = [
    { label: t('header.home'), path: '/' },
    { label: t('header.service'), path: '/service' },
    { label: dataSecurity.title }
  ]

  return (
    <ServiceLayout
      breadcrumbs={parentBreadcrumbs}
      title={dataSecurity.title}
    >
      <div className="space-y-12">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <p className="text-text-muted leading-relaxed text-lg">{dataSecurity.description}</p>
        </motion.section>

        {/* Transfer & storage options */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <h2 className="text-2xl font-display font-bold text-text-main mb-8">{dataSecurity.transferStorageOptions}</h2>

          <div className="space-y-6">
            {/* Box */}
            <div className="bio-card-static rounded-2xl overflow-hidden border border-border-light shadow-sm">
              <div className="bg-surface-50 px-4 sm:px-6 py-4 border-b border-border-light">
                <h3 className="font-display font-bold text-sm text-text-main flex items-center gap-2 flex-wrap break-words">
                  <Cloud className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{dataSecurity.tiers.standard.title}（{dataSecurity.tiers.standard.tier}）</span>
                </h3>
              </div>
              <div className="w-full">
                <div className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.howItWorks')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.standard.howItWorks}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.controls')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.standard.controls}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.whenToChoose')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.standard.whenToChoose}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.retention')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.standard.retention}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Amazon S3 */}
            <div className="bio-card-static rounded-2xl overflow-hidden border border-border-light shadow-sm">
              <div className="bg-surface-50 px-4 sm:px-6 py-4 border-b border-border-light">
                <h3 className="font-display font-bold text-sm text-text-main flex items-center gap-2 flex-wrap break-words">
                  <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{dataSecurity.tiers.enhanced.title}（{dataSecurity.tiers.enhanced.tier}）</span>
                </h3>
              </div>
              <div className="w-full">
                <div className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.howItWorks')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.enhanced.howItWorks}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.controls')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.enhanced.controls}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.whenToChoose')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.enhanced.whenToChoose}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.retention')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.enhanced.retention}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Encrypted HDD */}
            <div className="bio-card-static rounded-2xl overflow-hidden border border-border-light shadow-sm">
              <div className="bg-surface-50 px-4 sm:px-6 py-4 border-b border-border-light">
                <h3 className="font-display font-bold text-sm text-text-main flex items-center gap-2 flex-wrap break-words">
                  <HardDrive className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{dataSecurity.tiers.restricted.title}（{dataSecurity.tiers.restricted.tier}）</span>
                </h3>
              </div>
              <div className="w-full">
                <div className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.howItWorks')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.restricted.howItWorks}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.controls')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.restricted.controls}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.whenToChoose')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.restricted.whenToChoose}</span>
                    </li>
                    <li className="flex items-start gap-2.5 group">
                      <span className="text-sm font-bold text-text-main flex-shrink-0 leading-relaxed">{t('dataSecurity.retention')}:</span>
                      <span className="text-sm text-text-muted leading-relaxed">{dataSecurity.tiers.restricted.retention}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="font-display font-bold text-text-main mb-3">{t('technicalService.cta.dataSecurity.title')}</h3>
          <p className="text-sm text-text-muted mb-4" dangerouslySetInnerHTML={{ __html: t('technicalService.cta.dataSecurity.desc') }}></p>
        </motion.section>
      </div>
    </ServiceLayout>
  )
}

export default DataSecurityPage